import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { Environment } from '@environments/interface.environment';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { WPNavigationNode } from '../models/navigation-node';

@Injectable({
  providedIn: 'root'
})
export class NavigationNodeService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private httpHelper: HttpHelper,
              private cache: CacheService) { }

  private readonly cacheKey = 'NavigationNodeService-getNavigationNodesLight';
  public useCache = true;
  getAllNavigationNodes$: Observable<Array<WPNavigationNode>>;
  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  public reload() {
    if (this.useCache) {
      this.cache.removeFromPersistentCache(this.cacheKey);
    }
    this.reload$.next();
    this.getAllNavigationNodes$ = null;
    this.refresh$.next(null);
  }

  deleteNavigationNode(id: string): Observable<WPNavigationNode> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<WPNavigationNode>(`${Constants.API_ENDPOINT}/NavigationNodes/${id}`, {

      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleteNavigationNode response: `, response);
          this.reload();
          return response;
        }));

    return fetchOperation;
  }

  addNavigationNode(node: WPNavigationNode): Observable<WPNavigationNode> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<WPNavigationNode>(`${Constants.API_ENDPOINT}/NavigationNodes`, node, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`addNavigationNode added response: `, response);
          this.reload();
          return new WPNavigationNode(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'addNavigationNode', result: new WPNavigationNode(), rethrow: true })));

    return fetchOperation;
  }

  updateNavigationNode(id: string, node: WPNavigationNode): Observable<WPNavigationNode> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<WPNavigationNode>(`${Constants.API_ENDPOINT}/NavigationNodes/${id}`, node, {

      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updateNavigationNode response: `, response);
          this.reload();
          return new WPNavigationNode(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateNavigationNode', result: new WPNavigationNode(), rethrow: true })));

    return fetchOperation;
  }

  updateNavigationNodeOrder(nodes: Array<WPNavigationNode>): Observable<any> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<Array<WPNavigationNode>>(`${Constants.API_ENDPOINT}/NavigationNodes/`, nodes, {

      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updateNavigationNodeOrder ordering updated`);
          this.reload();
          return;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateNavigationNodeOrder', result: new WPNavigationNode(), rethrow: true })));

    return fetchOperation;
  }

  getNavigationNode(id: string): Observable<WPNavigationNode> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.get<WPNavigationNode>(`${Constants.API_ENDPOINT}/NavigationNodes/${id}`, {

      params: httpParams
    })
      .pipe(
        map(response => {
          console.log('getNavigationNode response: ', response);
          return new WPNavigationNode(response);
        }));

    return fetchOperation;
  }

  getAllNavigationNodes(): Observable<Array<WPNavigationNode>> {
    if (this.getAllNavigationNodes$) {
      return this.getAllNavigationNodes$;
    } else {
      this.getAllNavigationNodes$ = this.http.get<Array<WPNavigationNode>>(`${Constants.API_ENDPOINT}/NavigationNodes/`, {

      })
        .pipe(
          map(response => {
            console.log(`fetched result getAllNavigationNodes: `, response);
            return response.map(p => new WPNavigationNode(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(`${this.cacheKey}-all`, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllNavigationNodes', result: new Array<WPNavigationNode>(), rethrow: true, cacheKey: `${this.cacheKey}-all` })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<WPNavigationNode>>(`${this.cacheKey}-all`);
        if (cachedData) {
          return concat(of(cachedData), this.getAllNavigationNodes$);
        }
      }
      return this.getAllNavigationNodes$;
    }
  }
}
